/**
 * View-model for one row of the "Blocked users" settings screen.
 *
 * Threat model:
 *
 * The Settings → Blocked-users screen is a privacy-sensitive surface
 * because it materialises the answer to the question "who has this user
 * chosen to cut contact with?" That answer must NEVER appear on the
 * wire (covered separately by the deniable-block contract in
 * `BlockListStore` — the server has no API to ask). It must also never
 * be coloured by adversarial input: a malicious peer must not be able
 * to pick the string we display for them in OUR settings UI by
 * stuffing chosen bytes into a profile-name field. So this view model
 * has a hard rule:
 *
 *     The displayed name comes from the LOCAL trusted roster, or it
 *     comes from a userId-prefix stub. It NEVER comes from a
 *     peer-supplied profile name, profile bio, attachment caption,
 *     etc. There is no `peerClaimedName` parameter on
 *     `BlockedUserRowInputs` — the type system prevents one from
 *     leaking in.
 *
 * The view model is also pure-logic (no DOM, no IndexedDB) so the
 * self-test can exhaustively cover the (in-roster × name × age)
 * matrix without any environment.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockedUsersRowModel.swift`.
 */

import type { BlockListEntry, BlockReason } from "../block-list/BlockListEntry";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * One row rendered in the Blocked-users list.
 *
 * `userId` is the primary key so the UI can re-render without
 * ambiguity. `displayName` is what we show in the row title — either a
 * locally-trusted roster name or a userId prefix tagged "(Unknown user)".
 * `humanisedHint` is the secondary label ("blocked 3 days ago").
 * `isInRoster` lets the UI render a subtle indicator (e.g. dimmed avatar)
 * for unknowns. `blockedReason` passes through unchanged from the entry.
 */
export interface BlockedUserRow {
  readonly userId: string;
  readonly displayName: string;
  /** "blocked X ago" humanised string. */
  readonly humanisedHint: string;
  readonly isInRoster: boolean;
  readonly blockedReason: BlockReason;
}

/**
 * Inputs the row builder consumes.
 *
 * IMPORTANT: there is intentionally no `peerClaimedName` field. The
 * only naming source in this struct is `trustedDisplayName`, which
 * the caller MUST source from the local roster — never from a wire
 * payload. The builder treats `null | undefined` here as "unknown" and
 * falls back to a userId-prefix stub.
 */
export interface BlockedUserRowInputs {
  readonly entry: BlockListEntry;
  /** Local roster name only — NEVER peer-supplied. */
  readonly trustedDisplayName?: string | null;
  readonly isInRoster: boolean;
  /** Unix seconds (number) used to compute the "blocked X ago" hint. */
  readonly nowSeconds: number;
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

/**
 * Build one `BlockedUserRow` from `BlockedUserRowInputs`.
 *
 * Naming rule:
 * - If the peer is in the local roster AND we have a non-null
 *   `trustedDisplayName`, that name is used as-is.
 * - Otherwise the row shows a stub: `<first 8 chars of userId>… (Unknown user)`.
 *
 * This is the ONLY place row strings are derived. The caller cannot
 * inject peer-controlled strings.
 *
 * Mirrors `BlockedUserRowBuilder.build` in Swift.
 */
export function buildRow(inputs: BlockedUserRowInputs): BlockedUserRow {
  const displayName =
    inputs.isInRoster && inputs.trustedDisplayName != null
      ? inputs.trustedDisplayName
      : unknownStub(inputs.entry.userId);

  return {
    userId: inputs.entry.userId,
    displayName,
    humanisedHint: formatHint(inputs.entry.blockedAt, inputs.nowSeconds),
    isInRoster: inputs.isInRoster,
    blockedReason: inputs.entry.reason,
  };
}

// ---------------------------------------------------------------------------
// Helpers (exported for testing)
// ---------------------------------------------------------------------------

/**
 * `"<first 8 chars of userId>… (Unknown user)"` — never includes any
 * peer-supplied bytes.
 */
export function unknownStub(userId: string): string {
  const prefix = userId.slice(0, 8);
  return `${prefix}… (Unknown user)`;
}

/**
 * Humanise a "blocked X ago" hint. Buckets are deliberately coarse:
 * we don't want exact timestamps spilling into UI screen recordings,
 * and the user only needs a rough sense of "recent vs long ago."
 *
 * Future-dated entries (clock skew) are reported as "just now" rather
 * than "in the future" — a hostile peer cannot trigger any UI string
 * by clock-skewing because the entry's `blockedAt` is set on OUR device.
 */
export function formatHint(blockedAt: number, nowSeconds: number): string {
  const delta = Math.max(0, nowSeconds - blockedAt);
  if (delta < 60) return "just now";
  const minutes = Math.floor(delta / 60);
  if (minutes < 60)
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 365) return days === 1 ? "1 day ago" : `${days} days ago`;
  return "more than a year ago";
}
